/**
 * FlexibleWorkingApproval.jsx
 * 
 * Manager view for handling flexible working requests.
 * 
 * Features:
 * - Pending request queue with deadline tracking
 * - Acknowledge, schedule consultation, record consultation
 * - Approve (permanent or trial)
 * - Refuse (must select statutory reason + explanation)
 * - Deadline warnings for 2-month response requirement
 * - Appeal handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from './axiosConfig';
import FlexLetterDialog from './FlexLetterDialog';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  CircularProgress,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Schedule,
  ExpandMore,
  ExpandLess,
  Visibility,
  Event,
  ThumbUp,
  ThumbDown,
  Gavel,
  AccessTime,
  Person,
  Description,
  Refresh
} from '@mui/icons-material';

// ============================================================
// FLEXIBILITY TYPE NAMES
// ============================================================

const FLEXIBILITY_TYPE_NAMES = {
  part_time: 'Part-Time Working',
  compressed: 'Compressed Hours',
  flexitime: 'Flexitime',
  remote: 'Remote Working',
  hybrid: 'Hybrid Working',
  job_share: 'Job Share',
  term_time: 'Term-Time Only',
  other: 'Other Arrangement'
};

// ============================================================
// STATUS CONFIGURATIONS
// ============================================================

const STATUS_CONFIG = {
  submitted: { label: 'New', color: 'primary', action: 'Acknowledge' },
  acknowledged: { label: 'Acknowledged', color: 'info', action: 'Schedule Consultation' },
  consultation_scheduled: { label: 'Consultation Scheduled', color: 'warning', action: 'Record Consultation' },
  consultation_held: { label: 'Consultation Held', color: 'secondary', action: 'Make Decision' },
  approved: { label: 'Approved', color: 'success' },
  approved_trial: { label: 'Approved (Trial)', color: 'success' },
  refused: { label: 'Refused', color: 'error' },
  appealed: { label: 'Appealed', color: 'warning' },
  withdrawn: { label: 'Withdrawn', color: 'default' }
};

// ============================================================
// URGENCY COLORS
// ============================================================

const URGENCY_COLORS = {
  overdue: 'error.main',
  critical: 'error.light',
  urgent: 'warning.main',
  warning: 'warning.light',
  normal: 'grey.300'
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const FlexibleWorkingApproval = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [refusalReasons, setRefusalReasons] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [letterRequestId, setLetterRequestId] = useState(null);
  
  // Dialog states
  const [consultationDialog, setConsultationDialog] = useState({ open: false, request: null });
  const [recordConsultationDialog, setRecordConsultationDialog] = useState({ open: false, request: null });
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null });
  const [refuseDialog, setRefuseDialog] = useState({ open: false, request: null });

  // Load data
  useEffect(() => {
    loadPendingRequests();
    loadRefusalReasons();
  }, []);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/flexible-working/pending');
      setRequests(response.data.requests || []);
    } catch (err) {
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const loadRefusalReasons = async () => {
    try {
      const response = await api.get('/flexible-working/refusal-reasons');
      setRefusalReasons(response.data || []);
    } catch (err) {
      console.error('Failed to load refusal reasons');
    }
  };

  // Filter requests by tab
  const filteredRequests = requests.filter(r => {
    if (tabValue === 0) return true; // All pending
    if (tabValue === 1) return r.status === 'submitted';
    if (tabValue === 2) return ['consultation_scheduled', 'consultation_held'].includes(r.status);
    if (tabValue === 3) return r.urgency === 'overdue' || r.urgency === 'critical';
    return true;
  });

  // ============================================================
  // ACTION HANDLERS
  // ============================================================

  const handleAcknowledge = async (requestId) => {
    try {
      await api.post(`/flexible-working/requests/${requestId}/acknowledge`);
      loadPendingRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to acknowledge');
    }
  };

  const handleScheduleConsultation = async (data) => {
    try {
      await api.post(`/flexible-working/requests/${consultationDialog.request.id}/schedule-consultation`, data);
      setConsultationDialog({ open: false, request: null });
      loadPendingRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to schedule consultation');
    }
  };

  const handleRecordConsultation = async (data) => {
    try {
      await api.post(`/flexible-working/requests/${recordConsultationDialog.request.id}/record-consultation`, data);
      setRecordConsultationDialog({ open: false, request: null });
      loadPendingRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record consultation');
    }
  };

  const handleApprove = async (data) => {
    try {
      const id = approveDialog.request.id;
      await api.post(`/flexible-working/requests/${id}/approve`, data);
      setApproveDialog({ open: false, request: null });
      setLetterRequestId(id);
      loadPendingRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleRefuse = async (data) => {
    try {
      const id = refuseDialog.request.id;
      await api.post(`/flexible-working/requests/${id}/refuse`, data);
      setRefuseDialog({ open: false, request: null });
      setLetterRequestId(id);
      loadPendingRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to refuse');
    }
  };

  // ============================================================
  // RENDER REQUEST CARD
  // ============================================================

  const renderRequestCard = (req) => {
    const isExpanded = expandedId === req.id;
    const statusConfig = STATUS_CONFIG[req.status] || {};

    return (
      <Card 
        key={req.id} 
        variant="outlined" 
        sx={{ 
          mb: 2,
          borderLeft: 4,
          borderLeftColor: URGENCY_COLORS[req.urgency] || 'grey.300'
        }}
      >
        <CardContent>
          {/* Header Row */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h6">
                {FLEXIBILITY_TYPE_NAMES[req.flexibility_type] || req.flexibility_type}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {req.employee_name}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Chip 
                label={statusConfig.label} 
                color={statusConfig.color} 
                size="small" 
                sx={{ mb: 1 }}
              />
              {req.days_until_deadline !== null && (
                <Typography 
                  variant="caption" 
                  display="block"
                  color={req.urgency === 'overdue' ? 'error.main' : 
                         req.urgency === 'critical' ? 'error.main' : 'text.secondary'}
                  fontWeight={req.urgency === 'overdue' || req.urgency === 'critical' ? 'bold' : 'normal'}
                >
                  <AccessTime fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  {req.days_until_deadline < 0 
                    ? `${Math.abs(req.days_until_deadline)} days OVERDUE`
                    : req.days_until_deadline === 0 
                      ? 'Due TODAY'
                      : `${req.days_until_deadline} days to respond`}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Summary Row */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Submitted</Typography>
              <Typography variant="body2">
                {req.submitted_at ? new Date(req.submitted_at).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Proposed Start</Typography>
              <Typography variant="body2">
                {req.proposed_start_date ? new Date(req.proposed_start_date).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">Response Deadline</Typography>
              <Typography variant="body2" color={req.urgency === 'overdue' ? 'error.main' : 'inherit'}>
                {req.response_deadline ? new Date(req.response_deadline).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Grid>
          </Grid>

          {/* Expandable Details */}
          <Collapse in={isExpanded}>
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Current Pattern</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', backgroundColor: 'grey.50', p: 1, borderRadius: 1 }}>
                  {req.current_pattern}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Requested Pattern</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', backgroundColor: 'primary.lighter', p: 1, borderRadius: 1 }}>
                  {req.requested_pattern}
                </Typography>
              </Grid>
              
              {req.employee_reason && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Employee's Reason</Typography>
                  <Typography variant="body2">{req.employee_reason}</Typography>
                </Grid>
              )}
              
              {req.employee_impact_notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Employee's Impact Assessment</Typography>
                  <Typography variant="body2">{req.employee_impact_notes}</Typography>
                </Grid>
              )}

              {req.consultation_date && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <strong>Consultation:</strong> {new Date(req.consultation_date).toLocaleString('en-GB')}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Collapse>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button 
            size="small" 
            onClick={() => setExpandedId(isExpanded ? null : req.id)}
            endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
          >
            {isExpanded ? 'Less' : 'More Details'}
          </Button>

          <Box>
            {/* Status-specific actions */}
            {req.status === 'submitted' && (
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => handleAcknowledge(req.id)}
                sx={{ mr: 1 }}
              >
                Acknowledge
              </Button>
            )}

            {req.status === 'acknowledged' && (
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<Event />}
                onClick={() => setConsultationDialog({ open: true, request: req })}
                sx={{ mr: 1 }}
              >
                Schedule Consultation
              </Button>
            )}

            {req.status === 'consultation_scheduled' && (
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setRecordConsultationDialog({ open: true, request: req })}
                sx={{ mr: 1 }}
              >
                Record Consultation
              </Button>
            )}

            {req.status === 'consultation_held' && (
              <>
                <Button 
                  variant="contained" 
                  color="success"
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => setApproveDialog({ open: true, request: req })}
                  sx={{ mr: 1 }}
                >
                  Approve
                </Button>
                <Button 
                  variant="contained" 
                  color="error"
                  size="small"
                  startIcon={<ThumbDown />}
                  onClick={() => setRefuseDialog({ open: true, request: req })}
                >
                  Refuse
                </Button>
              </>
            )}

            {/* Allow early approval without consultation */}
            {['submitted', 'acknowledged'].includes(req.status) && (
              <Tooltip title="Approve without consultation (if straightforward)">
                <Button 
                  variant="contained" 
                  color="success"
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => setApproveDialog({ open: true, request: req })}
                >
                  Approve
                </Button>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      </Card>
    );
  };

  // ============================================================
  // DIALOGS
  // ============================================================

  // Schedule Consultation Dialog
  const ScheduleConsultationDialog = () => {
    const [date, setDate] = useState('');
    const [location, setLocation] = useState('');

    return (
      <Dialog open={consultationDialog.open} onClose={() => setConsultationDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Consultation</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You must discuss this request with the employee before making a decision.
          </Alert>
          <TextField
            fullWidth
            type="datetime-local"
            label="Consultation Date & Time"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., Office, Video call, Phone"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsultationDialog({ open: false, request: null })}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => handleScheduleConsultation({ consultation_date: date, consultation_location: location })}
            disabled={!date}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Record Consultation Dialog
  const RecordConsultationDialog = () => {
    const [notes, setNotes] = useState('');
    const [attendees, setAttendees] = useState('');

    return (
      <Dialog open={recordConsultationDialog.open} onClose={() => setRecordConsultationDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Record Consultation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Consultation Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Record what was discussed..."
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Attendees"
            value={attendees}
            onChange={(e) => setAttendees(e.target.value)}
            placeholder="e.g., Manager, Employee, HR"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordConsultationDialog({ open: false, request: null })}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => handleRecordConsultation({ consultation_notes: notes, consultation_attendees: attendees })}
          >
            Record & Continue
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Approve Dialog
  const ApproveDialog = () => {
    const [isTrial, setIsTrial] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState('');
    const [trialEndDate, setTrialEndDate] = useState('');
    const [trialCriteria, setTrialCriteria] = useState('');

    return (
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="Effective Date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, mt: 1 }}
          />

          <FormControlLabel
            control={<Switch checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />}
            label="Approve on trial basis"
          />

          <Collapse in={isTrial}>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Trial End Date"
                value={trialEndDate}
                onChange={(e) => setTrialEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Success Criteria"
                value={trialCriteria}
                onChange={(e) => setTrialCriteria(e.target.value)}
                placeholder="What will determine if the trial is successful?"
              />
            </Box>
          </Collapse>

          <Alert severity="info" sx={{ mt: 2 }}>
            {isTrial 
              ? 'A trial period allows you to assess if the arrangement works before making it permanent.'
              : 'Approving this request will make the change permanent. A contract variation should be issued.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, request: null })}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={() => handleApprove({
              effective_date: effectiveDate,
              is_trial: isTrial,
              trial_end_date: trialEndDate,
              trial_success_criteria: trialCriteria
            })}
            disabled={!effectiveDate || (isTrial && !trialEndDate)}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Refuse Dialog
  const RefuseDialog = () => {
    const [reasonCode, setReasonCode] = useState('');
    const [explanation, setExplanation] = useState('');
    const [alternative, setAlternative] = useState('');

    const selectedReason = refusalReasons.find(r => r.code === reasonCode);

    return (
      <Dialog open={refuseDialog.open} onClose={() => setRefuseDialog({ open: false, request: null })} maxWidth="md" fullWidth>
        <DialogTitle>Refuse Request</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Important</AlertTitle>
            You can only refuse for one of the 8 statutory reasons below. 
            You must explain how this reason applies to this specific request.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Statutory Reason for Refusal</InputLabel>
            <Select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              label="Statutory Reason for Refusal"
            >
              {refusalReasons.map(reason => (
                <MenuItem key={reason.code} value={reason.code}>
                  {reason.short_text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedReason && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Full wording:</strong> {selectedReason.full_wording}
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="How does this reason apply?"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Explain specifically why this reason applies to this request..."
            required
            sx={{ mb: 2 }}
            helperText="Be specific about the business impact"
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Alternative Offered (Recommended)"
            value={alternative}
            onChange={(e) => setAlternative(e.target.value)}
            placeholder="Is there an alternative arrangement you could offer?"
            helperText="Offering an alternative shows you've genuinely considered the request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefuseDialog({ open: false, request: null })}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => handleRefuse({
              decision_reason_code: reasonCode,
              decision_explanation: explanation,
              alternative_offered: alternative
            })}
            disabled={!reasonCode || !explanation}
          >
            Refuse Request
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Flexible Working Requests</Typography>
        <Button startIcon={<Refresh />} onClick={loadPendingRequests}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{requests.length}</Typography>
              <Typography variant="caption" color="text.secondary">Total Pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary">
                {requests.filter(r => r.status === 'submitted').length}
              </Typography>
              <Typography variant="caption" color="text.secondary">New Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main">
                {requests.filter(r => ['consultation_scheduled', 'consultation_held'].includes(r.status)).length}
              </Typography>
              <Typography variant="caption" color="text.secondary">In Consultation</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="error">
                {requests.filter(r => r.urgency === 'overdue' || r.urgency === 'critical').length}
              </Typography>
              <Typography variant="caption" color="text.secondary">Urgent</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`All (${requests.length})`} />
        <Tab label={`New (${requests.filter(r => r.status === 'submitted').length})`} />
        <Tab label={`In Progress (${requests.filter(r => ['consultation_scheduled', 'consultation_held'].includes(r.status)).length})`} />
        <Tab label={`Urgent (${requests.filter(r => r.urgency === 'overdue' || r.urgency === 'critical').length})`} />
      </Tabs>

      {/* Request Cards */}
      {filteredRequests.length === 0 ? (
        <Alert severity="info">No requests in this category</Alert>
      ) : (
        filteredRequests.map(renderRequestCard)
      )}

      {/* Dialogs */}
      <ScheduleConsultationDialog />
      <RecordConsultationDialog />
      <ApproveDialog />
      <RefuseDialog />
      <FlexLetterDialog open={!!letterRequestId} requestId={letterRequestId} onClose={() => setLetterRequestId(null)} />
    </Box>
  );
};

export default FlexibleWorkingApproval;